import { useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Stack, Card, Text, Title, Group, Button, Badge, Skeleton, SimpleGrid } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, DataTable, EmptyState, TableShell } from '@repletoryreach/sdk-ui/data-display';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { settings, isApiError } from '@repletoryreach/sdk-api';
import { StatCard } from '../../../components/StatCard';
import { TwoLineCell } from '../../../components/TwoLineCell';
import { CopyableId } from '../../../components/CopyableId';
import { formatDateTime, formatInteger, formatRelative, humanBytes, humanDuration } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { useT } from '../../../i18n/I18nProvider';
import { SnapshotDetailDrawer } from './components/SnapshotDetailDrawer';
import { VerifiedBadge } from './components/VerifiedBadge';
import './backups.css';

const MUTED = 'var(--rr-color-text-muted)';
/** More history than anyone reads on one page, and still one request. */
const HISTORY_LIMIT = 50;
/** A backup older than this is worth a colour: the page exists to catch it. */
const STALE_HOURS = 24;
const VERY_STALE_HOURS = 48;

const CopyIcon = (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
    <rect x="4" y="4" width="9" height="10" rx="1.5" />
    <path d="M6 4V3a1.5 1.5 0 0 1 1.5-1.5h4A1.5 1.5 0 0 1 13 3v6" />
  </svg>
);
const ArchiveIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="4" width="18" height="4" rx="1" /><path d="M5 8v11h14V8M10 12h4" />
  </svg>
);
const ClockIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" />
  </svg>
);
const DiskIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <ellipse cx="12" cy="6" rx="7.5" ry="3" /><path d="M4.5 6v12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3V6" /><path d="M4.5 12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3" />
  </svg>
);
const DatabaseIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="4" width="18" height="6" rx="2" /><rect x="3" y="14" width="18" height="6" rx="2" /><path d="M7 7h.01M7 17h.01" />
  </svg>
);

/**
 * The whole purpose of this page is moving a command into a terminal, so each
 * one gets a copy button rather than asking the operator to select text out of
 * a card.
 */
function Command({ index, label, hint, command }: { index?: number; label: string; hint?: ReactNode; command: string }) {
  const t = useT();

  async function copy() {
    try {
      await navigator.clipboard.writeText(command);
      notifySuccess(t('common.copied'));
    } catch {
      // A denied clipboard is not worth an error toast: the text is on screen.
    }
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
        <Text role="bodySmall" fw={600}>
          {index !== undefined ? `${index}. ${label}` : label}
        </Text>
        <Button size="xs" variant="default" leftSection={CopyIcon} onClick={copy}>{t('action.copy')}</Button>
      </Group>
      <div className="rr-command-block">{command}</div>
      {hint && <Text role="caption" c={MUTED}>{hint}</Text>}
    </Stack>
  );
}

function Step({ index, title, detail, command }: { index: number; title: string; detail: string; command?: string }) {
  return (
    <div className="rr-backup-step">
      <span className="rr-backup-step__index" aria-hidden>{index}</span>
      <Stack gap="xs">
        <Text role="bodySmall" fw={600}>{title}</Text>
        <Text role="caption" c={MUTED}>{detail}</Text>
        {command && <div className="rr-command-block">{command}</div>}
      </Stack>
    </div>
  );
}

export default function BackupsPage() {
  const t = useT();
  const [selected, setSelected] = useState<settings.BackupSnapshot | null>(null);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'ops', 'backups', HISTORY_LIMIT],
    queryFn: () => settings.listBackupSnapshots({ limit: HISTORY_LIMIT })
  });

  const meta = data?.meta;
  const denied = isApiError(error) && error.status === 403;
  const failed = Boolean(error);
  const snapshots = (data?.data ?? []) as Indexed<settings.BackupSnapshot>[];
  // The service returns newest first, so the head of the list is the snapshot
  // every figure in the stat row describes.
  const newest = snapshots[0] ?? null;

  // A store nobody configured and a store that could not be read are opposite
  // states: the first is a setup step, the second is a backup that may not be
  // there. They are told apart here so the page can render them differently.
  const notConfigured = meta?.error === 'backup_store_not_configured';
  const unreachable = meta?.error === 'backup_store_unreachable';

  const ageHours = meta?.age_hours ?? null;
  const stale = ageHours !== null && ageHours > STALE_HOURS;
  const ageTone = ageHours === null
    ? 'neutral'
    : ageHours > VERY_STALE_HOURS ? 'error' : ageHours > STALE_HOURS ? 'warning' : 'success';

  // Absence and freshness must not render alike, so a page with no snapshot at
  // all says "none" rather than counting zero hours since one.
  const none = t('system.backups.stats.none');
  const stat = (value: string | null) => (failed ? t('common.notAvailable') : (value ?? none));

  const emptyDescription = notConfigured
    ? t('system.backups.history.emptyNotConfigured')
    : unreachable
      ? t('system.backups.history.emptyUnreachable')
      : t('system.backups.history.emptyHint');

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('system.title')}
        title={t('system.backups.title')}
        description={t('system.backups.description')}
      />

      {denied && (
        <Alert color="warning" title={t('system.backups.denied.title')}>{t('system.backups.denied.body')}</Alert>
      )}

      {failed && !denied && (
        <Alert color="error">
          <Group justify="space-between" align="center" style={{ width: '100%' }}>
            <span>{t('system.backups.loadError')}</span>
            <Button size="xs" variant="outline" onClick={() => refetch()}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      )}

      {/* A configured store the console could not read is a warning: the backup
          may be fine, and the console cannot say that it is. The server's own
          notice already names the location and carries the failure, so it is
          quoted rather than paraphrased. */}
      {unreachable && !failed && (
        <Alert color="warning" title={t('system.backups.unreachable.title')}>
          {meta?.notice ?? meta?.message ?? t('system.backups.unreachable.fallback')}
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, xl: 4 }} spacing="md">
        <StatCard
          icon={ArchiveIcon}
          tone="primary"
          label={t('system.backups.stats.snapshots')}
          value={stat(newest ? formatInteger(meta?.count ?? snapshots.length) : null)}
          hint={meta?.location
            ? t('system.backups.stats.snapshotsHint', { location: meta.location })
            : t('system.backups.stats.snapshotsHintNoStore')}
          loading={isLoading}
          withRule
        />
        <StatCard
          icon={ClockIcon}
          tone={ageTone}
          label={t('system.backups.stats.newest')}
          value={stat(meta?.latest_at ? formatDateTime(meta.latest_at) : null)}
          hint={ageHours === null
            ? t('system.backups.stats.noneHint')
            : t(stale ? 'system.backups.stats.newestStale' : 'system.backups.stats.newestAge', {
                age: humanDuration(ageHours * 3_600_000)
              })}
          loading={isLoading}
          withRule
        />
        <StatCard
          icon={DiskIcon}
          tone="info"
          label={t('system.backups.stats.size')}
          value={stat(newest ? humanBytes(newest.total_bytes) : null)}
          hint={newest?.total_live_rows != null
            ? t('system.backups.stats.sizeHint', { rows: formatInteger(newest.total_live_rows) })
            : undefined}
          loading={isLoading}
          withRule
        />
        <StatCard
          icon={DatabaseIcon}
          tone="neutral"
          label={t('system.backups.stats.databases')}
          value={stat(newest ? formatInteger(newest.database_count) : null)}
          hint={newest ? t('system.backups.stats.databasesHint') : undefined}
          loading={isLoading}
          withRule
        />
      </SimpleGrid>

      <div className="rr-backups-grid">
        <Card withBorder>
          <Stack gap="md">
            <Stack gap="xs">
              <Title order={5}>{t('system.backups.procedure.title')}</Title>
              <Text role="caption" c={MUTED}>{t('system.backups.procedure.hint')}</Text>
            </Stack>
            <Step
              index={1}
              title={t('system.backups.procedure.step1')}
              detail={t('system.backups.procedure.step1Detail')}
            />
            <Step
              index={2}
              title={t('system.backups.procedure.step2')}
              detail={t('system.backups.procedure.step2Detail')}
              command={t('system.backups.procedure.step2Command')}
            />
            <Step
              index={3}
              title={t('system.backups.procedure.step3')}
              detail={t('system.backups.procedure.step3Detail')}
              command={t('system.backups.procedure.step3Command')}
            />
            <Step
              index={4}
              title={t('system.backups.procedure.step4')}
              detail={t('system.backups.procedure.step4Detail')}
              command={t('system.backups.procedure.step4Command')}
            />
            {/* The page has no destructive button to confirm, so the warning
                sits beside the instructions that are destructive. */}
            <Alert color="warning" title={t('system.backups.destructive.title')}>
              {t('system.backups.destructive.body')}
            </Alert>
          </Stack>
        </Card>

        <Card withBorder>
          {/* Without an explicit loading branch the page showed "not yet
              provisioned" during the fetch and then replaced it with the server
              notice, so it briefly told the operator the opposite of the truth. */}
          {isLoading ? (
            <Stack gap="md">
              <Skeleton height={14} width="70%" />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
            </Stack>
          ) : (
            <Stack gap="lg">
              <Stack gap="xs">
                <Title order={5}>{t('system.backups.section.commands')}</Title>
                <Text role="caption" c={MUTED}>{t('system.backups.commandsIntro')}</Text>
              </Stack>
              {/* The notice describes whatever state the store is in, so it is
                  always present and cannot be used as the test. Only the
                  unconfigured case belongs here, where the commands that fix it
                  are beside it; the other states are reported by the alert above
                  or under the history table. */}
              {notConfigured && meta?.notice && <Alert color="info">{meta.notice}</Alert>}
              {meta?.command && (
                <Command
                  index={1}
                  label={t('system.backups.takeSnapshot')}
                  hint={t('system.backups.takeSnapshotHint')}
                  command={meta.command}
                />
              )}
              {meta?.restore && (
                <Command
                  index={2}
                  label={t('system.backups.restoreLatest')}
                  hint={t('system.backups.procedure.step2Detail')}
                  command={meta.restore}
                />
              )}
              {/* The drill is the script an operator most wants during a real
                  restore and the console never mentioned it. It is listed even
                  when the server's meta block does not name it yet. */}
              <Command
                index={3}
                label={t('system.backups.runDrill')}
                hint={t('system.backups.drillHint')}
                command={t('system.backups.drillCommand')}
              />
              <Command
                index={4}
                label={t('system.backups.reconcile')}
                hint={t('system.backups.reconcileHint')}
                command={t('system.backups.reconcileCommand')}
              />
            </Stack>
          )}
        </Card>
      </div>

      <TableShell
        title={t('system.backups.history.title')}
        description={t('system.backups.history.description')}
        totalLabel={meta?.location
          ? t('system.backups.history.total', { count: formatInteger(meta.count), location: meta.location })
          : undefined}
        // What "verified" does and does not prove, said next to the column that
        // claims it rather than in a tooltip nobody opens.
        belowToolbar={
          <Text role="caption" c={MUTED}>
            {meta?.verification === 'none'
              ? t('system.backups.verified.explainNone')
              : t('system.backups.verified.explain')}
          </Text>
        }
        loading={isLoading}
        isEmpty={!isLoading && !failed && snapshots.length === 0}
        empty={<EmptyState title={t('system.backups.history.empty')} description={emptyDescription} />}
        pagination={
          <Group justify="space-between" align="center" wrap="wrap" gap="sm" style={{ width: '100%' }}>
            {/* A store that answered for itself gets the server's one-line
                summary here, quietly. The two failure states are louder and
                are handled above. */}
            <Text role="caption" c={MUTED}>
              {!notConfigured && !unreachable && meta?.notice ? meta.notice : t('system.backups.history.rowHint')}
            </Text>
            {!notConfigured && !unreachable && meta?.notice && (
              <Text role="caption" c={MUTED}>{t('system.backups.history.rowHint')}</Text>
            )}
          </Group>
        }
      >
        <DataTable<Indexed<settings.BackupSnapshot>>
          records={snapshots}
          loading={isLoading}
          idAccessor="id"
          highlightOnHover
          emptyState={t('system.backups.history.empty')}
          onRowClick={(s) => setSelected(s)}
          columns={[
            {
              accessor: 'id',
              title: t('system.backups.history.columns.snapshot'),
              // The prefix is the argument an operator types into restore.sh,
              // so it is shown whole and copies whole.
              render: (s) => <CopyableId value={s.id} chars={20} />
            },
            {
              accessor: 'taken_at',
              title: t('system.backups.history.columns.taken'),
              render: (s) => s.taken_at
                ? <TwoLineCell primary={formatDateTime(s.taken_at)} secondary={formatRelative(s.taken_at)} title={formatDateTime(s.taken_at)} />
                : <Text role="caption" c={MUTED}>{t('common.unknown')}</Text>
            },
            {
              accessor: 'host',
              title: t('system.backups.history.columns.host'),
              render: (s) => s.host
                ? <TwoLineCell primary={s.host} secondary={s.container ?? undefined} title={s.host} />
                : <Text role="caption" c={MUTED}>{t('common.unknown')}</Text>
            },
            {
              accessor: 'database_count',
              title: t('system.backups.history.columns.databases'),
              textAlign: 'right',
              render: (s) => formatInteger(s.database_count)
            },
            {
              accessor: 'total_bytes',
              title: t('system.backups.history.columns.size'),
              textAlign: 'right',
              render: (s) => (
                <TwoLineCell
                  primary={humanBytes(s.total_bytes)}
                  secondary={s.total_live_rows != null
                    ? t('system.backups.stats.sizeHint', { rows: formatInteger(s.total_live_rows) })
                    : undefined}
                />
              )
            },
            {
              accessor: 'encryption',
              title: t('system.backups.history.columns.encryption'),
              render: (s) => (
                <Badge size="xs" variant="light" color={s.encryption === 'none' ? 'gray' : 'green'}>
                  {s.encryption === 'none' ? t('system.backups.encryption.none') : s.encryption}
                </Badge>
              )
            },
            {
              accessor: 'verified',
              title: t('system.backups.history.columns.verified'),
              render: (s) => <VerifiedBadge snapshot={s} withDetail />
            }
          ]}
        />
      </TableShell>

      <SnapshotDetailDrawer
        snapshot={selected}
        verification={meta?.verification}
        onClose={() => setSelected(null)}
      />
    </Stack>
  );
}
